import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Upload, Trash2, Loader2, ImagePlus } from "lucide-react";

const CATEGORIES = ["Our Fleet", "Warehousing", "On-Road Operations"];
const imgSrc = (g) => g.image_url || `${API_BASE}/gallery/image/${g.id}`;

export default function GalleryAdmin() {
  const [images, setImages] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setImages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose an image file");
    if (!title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      fd.append("description", description);
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded — live on the public gallery!");
      setFile(null); setTitle(""); setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.title}" from the gallery?`)) return;
    try {
      await api.delete(`/gallery/${g.id}`);
      toast.success("Image removed");
      setImages((imgs) => imgs.filter((x) => x.id !== g.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b5cc4]";

  return (
    <div className="p-6 lg:p-8" data-testid="gallery-admin-page">
      <h1 className="text-xl font-semibold text-slate-900">Website Gallery</h1>
      <p className="text-sm text-slate-500 mt-1">Upload photos here — they instantly appear on the public website gallery.</p>

      <form onSubmit={upload} className="mt-6 bg-white border border-slate-200 rounded-xl p-6 max-w-2xl" data-testid="gallery-upload-form">
        <div className="flex items-center gap-2 text-slate-800 font-medium mb-4"><ImagePlus size={18} /> Upload New Image</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Image File *</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-600 file:mr-3 file:px-4 file:py-2 file:rounded-md file:border-0 file:bg-[#0b5cc4] file:text-white file:text-sm file:cursor-pointer"
              data-testid="gallery-file-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Title *</label>
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New Tata Prima Truck" data-testid="gallery-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Category *</label>
            <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-category-select">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Description</label>
            <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional short description" data-testid="gallery-description-input" />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="gallery-upload-btn"
          className="mt-5 btn-primary px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {busy ? "Uploading…" : "Upload Image"}
        </button>
      </form>

      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Existing Photos ({images.length})</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((g) => (
            <div key={g.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden group" data-testid={`admin-gallery-item-${g.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                <img src={imgSrc(g)} alt={g.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-slate-900 truncate">{g.title}</div>
                <div className="text-[11px] text-slate-500">{g.category}</div>
                <button onClick={() => remove(g)} data-testid={`admin-gallery-delete-${g.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
        {images.length === 0 && <div className="text-sm text-slate-400 mt-4">No photos yet. Upload your first image above.</div>}
      </div>
    </div>
  );
}
